import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import { normalizeRichHtmlSpacing } from '../utils/collapseQuillBlankParagraphs';
import './RichContent.css';

/** WYSIWYG / pasted content: real HTML tags (not e.g. "2 < 3") */
const LOOKS_LIKE_HTML_TAGS = /<\/?[a-z][a-z0-9]*\b/i;

const TABLE_TAGS = ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col'];

let richContentStyleHookInstalled = false;
if (!richContentStyleHookInstalled) {
  richContentStyleHookInstalled = true;
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName !== 'style' || !node?.tagName) return;
    const v = String(data.attrValue || '').trim();
    if (!v) {
      data.keepAttr = false;
      return;
    }
    const parts = v.split(';').map((s) => s.trim()).filter(Boolean);
    const allowed = [];
    const isImg = node.tagName.toUpperCase() === 'IMG';
    for (const p of parts) {
      const m = /^([\w-]+)\s*:\s*(.+)$/i.exec(p);
      if (!m) continue;
      const prop = m[1].toLowerCase();
      const val = m[2].trim();
      if (/url\s*\(/i.test(val) || /expression\s*\(/i.test(val)) continue;

      if (isImg) {
        const allowedImgProps = new Set([
          'width', 'height', 'max-width', 'min-width', 'max-height', 'min-height',
          'float', 'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
          'display', 'aspect-ratio', 'object-fit'
        ]);
        if (allowedImgProps.has(prop)) {
          allowed.push(`${prop}: ${val}`);
          continue;
        }
      }

      if (prop !== 'color' && prop !== 'background-color') continue;
      if (!/^[#\w().%,\s+-]+$/.test(val)) continue;
      allowed.push(`${prop}: ${val}`);
    }
    if (!allowed.length) {
      data.keepAttr = false;
      return;
    }
    data.attrValue = allowed.join('; ');
  });
}

const HTML_SANITIZE = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'span', 'div',
    'blockquote', 'pre', 'code', 'hr',
    'img', 'iframe', 'video', 'source',
    ...TABLE_TAGS
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'src', 'alt', 'class', 'style',
    'colspan', 'rowspan', 'width', 'height',
    'allow', 'allowfullscreen', 'frameborder', 'controls', 'poster', 'title', 'playsinline'
  ]
};

/**
 * Renders content as HTML (sanitized) or Markdown.
 * - If content contains HTML tags, sanitize and render as HTML first (fixes mixed HTML + markdown
 *   from editors: ReactMarkdown would show raw <p>… as text).
 * - Else if content looks like Markdown, use ReactMarkdown.
 * - Otherwise render as Markdown (plain text becomes a paragraph).
 */
const RichContent = ({ content, className = '', stripQuillBlankParagraphs = true }) => {
  if (!content || typeof content !== 'string') return null;

  const trimmed = content.trim();
  if (!trimmed) return null;

  const looksLikeHtml = LOOKS_LIKE_HTML_TAGS.test(trimmed);

  if (looksLikeHtml) {
    const rawHtml = stripQuillBlankParagraphs ? normalizeRichHtmlSpacing(trimmed) : trimmed;
    const sanitized = DOMPurify.sanitize(rawHtml, HTML_SANITIZE);
    return (
      <div
        className={`rich-content rich-content-html ${className}`.trim()}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  return (
    <div className={`rich-content rich-content-markdown ${className}`.trim()}>
      <ReactMarkdown
        components={{
          a: ({ href, children, ...props }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
          ),
          img: ({ src, alt, ...props }) => (
            <img src={src} alt={alt || ''} className="rich-content-inline-img" {...props} />
          )
        }}
      >
        {trimmed}
      </ReactMarkdown>
    </div>
  );
};

export default RichContent;
